import { useState } from "react";
import { Box, Switch, TextField, Typography, IconButton, Button, Divider, Select, MenuItem, FormControl, InputLabel, ToggleButton, ToggleButtonGroup, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { toast } from "react-toastify";
import { compressImage } from "../../../../../../../utils/imageCompressor";

const glassCard = { p: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)", border: "1px solid rgba(229,231,235,0.6)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" };
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } };
const accentText = (a) => ({ ...fieldSx, "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: a }, "& .MuiInputLabel-root.Mui-focused": { color: a } });

const OrderItemsTab = ({ skipOption, setSkipOption, errorMessage, setErrorMessage, options, setOptions, validations, setValidations, accentColor = "#8b5cf6" }) => {
  const items = (options || []).map((o) => (typeof o === "object" ? o : { name: String(o), price: 0, qty: 1, image: "", buttonText: "Add" }));
  const currency = validations?.orderCurrency || "₹";
  const minItems = validations?.minItems;

  const update = (next) => setOptions(next.length ? next : [emptyItem()]);
  const emptyItem = () => ({ name: "", price: 0, qty: 1, image: "", buttonText: "Add" });
  const addItem = () => update([...items, emptyItem()]);
  const removeItem = (i) => update(items.filter((_, idx) => idx !== i));
  const changeItem = (i, patch) => {
    const u = [...items];
    u[i] = { ...u[i], ...patch };
    update(u);
  };

  const handleImageUpload = (i) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const compressed = await compressImage(file, 200, 0.6);
        changeItem(i, { image: compressed });
      } catch {
        toast.error("Failed to compress image");
      }
    };
    input.click();
  };

  const total = items.reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.qty) || 0), 0);

  return (
    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Skip Option</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Allow visitors to skip</Typography>
          </Box>
          <Switch checked={!!skipOption} onChange={(e) => setSkipOption(e.target.checked)} size="small" />
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Order Items</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Product list with image, price, quantity. Tap to add to cart.</Typography>
          </Box>
          <Button
            size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={addItem}
            sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, borderRadius: 2 }}
          >
            Add Item
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          {items.map((it, i) => (
            <Box key={i} sx={{ p: 1.5, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: `${accentColor}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: accentColor }}>{i + 1}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{it.name || `Item ${i + 1}`}</Typography>
                </Box>
                <IconButton size="small" onClick={() => removeItem(i)} sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", gap: 1, mb: 0.8 }}>
                <Box
                  onClick={() => handleImageUpload(i)}
                  sx={{
                    width: 56, height: 56, borderRadius: 1.5, flexShrink: 0,
                    border: `1.5px dashed ${it.image ? accentColor : "#d0d0d0"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", overflow: "hidden", bgcolor: "#fafafa", transition: "all 0.2s",
                    "&:hover": { borderColor: accentColor, transform: "scale(1.04)" },
                  }}
                >
                  {it.image ? (
                    <img src={it.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <AddPhotoAlternateIcon sx={{ color: "#bbb", fontSize: 20 }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.8 }}>
                  <TextField
                    fullWidth size="small" placeholder="Item name"
                    value={it.name || ""}
                    onChange={(e) => changeItem(i, { name: e.target.value })}
                    sx={accentText(accentColor)}
                  />
                  <Box sx={{ display: "flex", gap: 0.8 }}>
                    <TextField
                      fullWidth size="small" type="number" label="Price"
                      value={it.price ?? 0}
                      onChange={(e) => changeItem(i, { price: Number(e.target.value) || 0 })}
                      sx={fieldSx}
                    />
                    <TextField
                      fullWidth size="small" type="number" label="Qty"
                      value={it.qty ?? 1}
                      onChange={(e) => changeItem(i, { qty: Number(e.target.value) || 1 })}
                      sx={fieldSx}
                    />
                  </Box>
                </Box>
              </Box>

              <TextField
                fullWidth size="small" placeholder='Button text (default "Add")'
                value={it.buttonText || ""}
                onChange={(e) => changeItem(i, { buttonText: e.target.value })}
                sx={fieldSx}
              />
            </Box>
          ))}
        </Box>

        {items.length === 0 && (
          <Box sx={{ p: 3, textAlign: "center", color: "#9ca3af", fontSize: 12, border: "1.5px dashed #e5e7eb", borderRadius: 2 }}>
            No items yet. Click "Add Item" to start.
          </Box>
        )}

        {items.length > 0 && total > 0 && (
          <Box sx={{ mt: 1.5, p: 1.2, bgcolor: `${accentColor}10`, borderRadius: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Preview Total</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: accentColor }}>{currency}{total.toLocaleString()}</Typography>
          </Box>
        )}
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1.5, color: "#1f2937" }}>Display & Validation</Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel sx={{ "&.Mui-focused": { color: accentColor } }}>Currency</InputLabel>
            <Select
              value={currency}
              label="Currency"
              onChange={(e) => setValidations({ ...validations, orderCurrency: e.target.value })}
              sx={{ borderRadius: 2, bgcolor: "#fff", fontSize: 12, "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.1)" }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: accentColor } }}
            >
              {["₹", "$", "€", "£", "¥", "AED", "₩"].map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth size="small" type="number" label="Min items to order"
            value={minItems ?? ""}
            onChange={(e) => setValidations({ ...validations, minItems: e.target.value === "" ? undefined : Number(e.target.value) })}
            sx={fieldSx}
          />
        </Box>
        <Typography sx={{ fontSize: 10, color: "#6b7280" }}>Min items forces visitor to add at least N items before proceeding.</Typography>
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Error Message</Typography>
        <TextField fullWidth placeholder="Please add at least one item" value={errorMessage || ""} size="small"
          onChange={(e) => setErrorMessage(e.target.value)}
          sx={fieldSx} />
      </Box>
    </Box>
  );
};

export default OrderItemsTab;
